import { DEFAULT_RISK_SETTINGS } from '../../../constants/riskConfig.js';

/** The draft the onboarding wizard starts from; the wizard owns the state. */
export const DEFAULT_RISK_DRAFT = {
  name: 'Reference checks not completed before joiner start date',
  description:
    'There is no confirmed process for verifying that reference checks are completed prior to a new employee starting. This could result in hiring individuals who misrepresented their background.',
  likelihood: 3,
  impact: 3,
};

/**
 * Controlled step body. The write runs in the wizard's `validate()` for this
 * step; once it has run, `createdId` locks the fields so going Back shows the
 * saved risk instead of offering to create a second one.
 */
export default function RiskCreateStep({ value, onChange, state, createdId }) {
  const locked = Boolean(createdId);
  const set = (patch) => onChange({ ...value, ...patch });
  const settings = state.riskSettings || DEFAULT_RISK_SETTINGS;
  const likelihoodLvls = settings.likelihoodLevels || DEFAULT_RISK_SETTINGS.likelihoodLevels;
  const impactLvls = settings.impactLevels || DEFAULT_RISK_SETTINGS.impactLevels;

  return (
    <div>
      <h2
        style={{
          fontSize: 20,
          fontWeight: 700,
          color: 'var(--fg)',
          marginBottom: 8,
          fontFamily: 'var(--font-display)',
        }}
      >
        Your first risk
      </h2>
      <p style={{ fontSize: 14, color: 'var(--fg2)', lineHeight: 1.65, marginBottom: 20 }}>
        {locked
          ? `Saved as ${createdId}. You can change it later in the risk register.`
          : "Here's a risk based on your Joiner process. Review it and adjust if needed, then press Next to add it."}
      </p>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Risk name</label>
        <input
          value={value.name}
          onChange={(e) => set({ name: e.target.value })}
          disabled={locked}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Description</label>
        <textarea
          value={value.description}
          onChange={(e) => set({ description: e.target.value })}
          disabled={locked}
          rows={4}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 0 }}>
        <LevelSelector
          label="Likelihood"
          value={value.likelihood}
          levels={likelihoodLvls}
          onChange={(likelihood) => set({ likelihood })}
          disabled={locked}
        />
        <LevelSelector
          label="Impact"
          value={value.impact}
          levels={impactLvls}
          onChange={(impact) => set({ impact })}
          disabled={locked}
        />
      </div>
    </div>
  );
}

function LevelSelector({ label, value, levels, onChange, disabled }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {(levels || []).map((l) => (
          <button
            key={l.value}
            type="button"
            onClick={() => onChange(l.value)}
            disabled={disabled}
            aria-pressed={value === l.value}
            style={{
              padding: '6px 10px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: disabled ? 'default' : 'pointer',
              border: `1px solid ${value === l.value ? l.color + '66' : 'var(--border)'}`,
              background: value === l.value ? l.color + '1a' : 'var(--bg3)',
              color: value === l.value ? l.color : 'var(--fg3)',
              transition: 'all 0.12s',
            }}
          >
            {l.value}
          </button>
        ))}
      </div>
      {levels && (
        <div style={{ fontSize: 11, color: 'var(--fg3)', marginTop: 4 }}>
          {levels.find((l) => l.value === value)?.label || ''}
        </div>
      )}
    </div>
  );
}

const labelStyle = {
  display: 'block',
  marginBottom: 6,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  color: 'var(--fg3)',
  fontFamily: 'var(--font-mono)',
};

const inputStyle = {
  width: '100%',
  background: 'var(--bg3)',
  border: '1px solid var(--border2)',
  borderRadius: 8,
  padding: '10px 12px',
  color: 'var(--fg)',
  fontSize: 13,
  fontFamily: 'var(--font-body)',
  lineHeight: 1.5,
  outline: 'none',
};
